import React, { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Container } from 'reactstrap';

import CenteredLoader from '../../components/CenteredLoader/CenteredLoader';
import CoursesList from '../../components/CoursesList/CoursesList';
import Helmet from '../../components/common/Helmet';
import Map from '../../containers/Map';
import { sortPromotedGolfCourses } from '../../helpers/sortPromotedGolfCourses';
import usePopularGolfCourses from '../../hooks/usePopularGolfCourses';
import useTranslate from '../../hooks/useTranslate';
import isGolfCourseNearCity from '../../services/apis/search/isGolfCourseNearCity';
import useSearchData from '../../services/apis/search/useSearchData';
import useStore from '../../store/useStore';

const CityPage: FC = () => {
  const params = useParams();
  const cityName = params.city;
  const { translate } = useTranslate();
  const { golfCourses, cities, isLoading } = useSearchData();
  const { activeGolfCourseId } = useStore(state => state);
  const currentCity = useMemo(
    () => cities?.find(city => city.name === cityName),
    [cities, cityName]
  );
  const mapCourses = useMemo(
    () =>
      golfCourses?.map(golfCourse => ({
        ...golfCourse,
        marked: golfCourse && currentCity ? isGolfCourseNearCity(golfCourse, currentCity) : false,
      })),
    [golfCourses, currentCity]
  );
  const popularGolfCourses = usePopularGolfCourses();

  const courses = useMemo(() => {
    const cityCourses = mapCourses?.filter(g => g.marked);

    return sortPromotedGolfCourses(cityCourses);
  }, [mapCourses]);
  const nrOfCourses = courses?.length || 0;

  return (
    <>
      {currentCity && (
        <>
          <Helmet
            titleTranslationKey="ClubListContainer.Title"
            titleData={{
              name: currentCity?.name || '',
              amount: nrOfCourses,
            }}
            descriptionTranslationKey="ClubListContainer.helmetDescription"
            descriptionData={{
              name: currentCity?.name || '',
              amount: nrOfCourses,
            }}
          />
          {!isLoading && mapCourses && (
            <>
              <div className="mt-2 mb-4">
                <Map courses={mapCourses} hoveredCourse={activeGolfCourseId} />
              </div>
              <Container>
                <h1 className="display-5 mb-4">{currentCity.name}</h1>
                <h2 className="mb-4">
                  {translate('City.ClubList.Title', {
                    amount: nrOfCourses,
                    name: currentCity.name,
                  })}
                </h2>

                {courses && courses.length > 0 && (
                  <CoursesList
                    courses={courses}
                    className={'mb-5'}
                    topRatedGolfCourses={
                      popularGolfCourses.find(item => item.country.id === currentCity?.countryId)
                        ?.topRated
                    }
                    topBookedGolfCourses={
                      popularGolfCourses.find(item => item.country.id === currentCity?.countryId)
                        ?.topBooked
                    }
                  />
                )}
              </Container>
            </>
          )}
        </>
      )}
      {isLoading && <CenteredLoader message={String(translate('Courses.LoadingGolfCourses'))} />}
    </>
  );
};

export default CityPage;
